import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link as RouterLink, useLocation } from "react-router-dom";
import bkstApi from "../../api";
import {
  anyWordStartsWith,
  getQueryParam,
} from "../../common/util";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../../common/constants";
import currency from "currency.js";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SearchIcon from "@mui/icons-material/Search";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import moment from "moment";

const FILTERS = ["active", "draft"];

const getSessionPriceRange = (event) => {
  const sessions = event?.options?.session?.options || [];
  const amounts = sessions
    .map((s) => +s.pricing?.amount)
    .filter((n) => !Number.isNaN(n));
  if (amounts.length === 0) {
    return null;
  }
  return { min: Math.min(...amounts), max: Math.max(...amounts) };
};

const ActionsCell = ({ event, onEventUpdate }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const toggleStatus = () => {
    const newStatus = event.status === "ACTIVE" ? "DRAFT" : "ACTIVE";

    bkstApi
      .put(`/my/class/${event.id}`, { status: newStatus })
      .then(() => {
        onEventUpdate(event.id, { status: newStatus });
        handleClose();
      })
      .catch(() => {});
  };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} title="More options">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem sx={{ padding: "20px" }} onClick={toggleStatus}>
          <PowerSettingsNewIcon sx={{ mr: 1, fontSize: 20 }} />
          {event.status === "ACTIVE" ? "Deactivate" : "Activate"}
        </MenuItem>
        <MenuItem
          sx={{ padding: "20px" }}
          component={RouterLink}
          to={`/events/${event.id}`}
        >
          <EditOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
      </Menu>
    </>
  );
};

const getColumns = (handleEventUpdate) => [
  {
    field: "image",
    headerName: "Image",
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <Box sx={{ position: "relative" }}>
        <img
          src={params.row.images?.[0] || `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`}
          style={{
            width: "60px",
            height: "60px",
            objectFit: "cover",
            borderRadius: "4px",
          }}
          alt={params.row.title}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "4px",
            right: "4px",
            width: "12px",
            height: "12px",
            backgroundColor: params.row.status === "ACTIVE" ? "#16a085" : "#c0392b",
            borderRadius: "50%",
            border: "1px solid white",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        />
      </Box>
    ),
  },
  {
    field: "title",
    headerName: "Event",
    flex: 1,
    minWidth: 200,
    renderCell: (params) => {
      const priceRange = getSessionPriceRange(params.row);
      const sessionCount = params.row.options?.session?.options?.length || 0;

      return (
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1">{params.value}</Typography>
          <Typography variant="body2" color="textSecondary">
            {priceRange
              ? priceRange.max > priceRange.min
                ? `${currency(priceRange.min).format()} - ${currency(priceRange.max).format()}`
                : currency(priceRange.min).format()
              : "No price"}
            {sessionCount > 0 ? ` · ${sessionCount} session${sessionCount === 1 ? "" : "s"}` : ""}
          </Typography>
        </Box>
      );
    },
  },
  {
    field: "nextSession",
    headerName: "Next Session",
    width: 160,
    sortable: false,
    valueGetter: (params) => {
      const today = moment().format("YYYY-MM-DD");
      const dates = (params.row.options?.session?.options || [])
        .map((s) => s.date)
        .filter(Boolean)
        .sort();
      return dates.find((d) => d >= today) || dates[0] || "";
    },
    renderCell: (params) => (
      <Typography variant="body2">
        {params.value ? moment(params.value).format("MMM D, YYYY") : "—"}
      </Typography>
    ),
  },
  {
    field: "actions",
    headerName: "Action",
    width: 80,
    sortable: false,
    renderCell: (params) => (
      <ActionsCell event={params.row} onEventUpdate={handleEventUpdate} />
    ),
  },
];

export default function Events() {
  const location = useLocation();

  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const f = getQueryParam(location.search, "filter");
    setFilter(FILTERS.includes(f) ? f : "active");
  }, [location.search]);

  useEffect(() => {
    setLoading(true);
    bkstApi(`/my/class?filter=${filter}`).then((res) => {
      setEvents(res.data.products || []);
      setLoading(false);
    });
  }, [filter]);

  useEffect(() => {
    let next = events;
    if (query) {
      next = next.filter((o) => anyWordStartsWith(o.title, query));
    }
    setFiltered(next);
  }, [events, query]);

  const handleEventUpdate = (eventId, updates) => {
    setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, ...updates } : e)));
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box>
      <Container maxWidth="lg">
        <Box mt={6}>
          <Box sx={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
            <Box>
              <Typography variant="h1" gutterBottom>
                Events
              </Typography>
            </Box>
            <Button variant="contained" color="primary" component={RouterLink} to="/events/add">
              Add Event
            </Button>
          </Box>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Create booking pages for classes listed on your website
          </Typography>
          <Box my={2}>
            <Tabs
              value={filter}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: "1px solid #ECECEC" }}
            >
              <Tab
                value="active"
                label="Active"
                component={RouterLink}
                to="/events?filter=active"
              />
              <Tab value="draft" label="Draft" component={RouterLink} to="/events?filter=draft" />
            </Tabs>
          </Box>

          {events.length > 0 && (
            <Box my={2}>
              <Paper elevation={0}>
                <Box py={0.75} px={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <TextField
                    id="query"
                    value={query}
                    name="query"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Filter by Name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment sx={{ paddingRight: "10px" }}>
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment>
                          {query && (
                            <IconButton onClick={() => setQuery("")}>
                              <CloseOutlinedIcon />
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "25px",
                      },
                    }}
                  />
                </Box>
              </Paper>

              <Box my={4} sx={{ height: "auto", width: "100%" }}>
                <DataGrid
                  rows={filtered.map((event, index) => ({
                    id: event.id || index,
                    ...event,
                  }))}
                  columns={getColumns(handleEventUpdate)}
                  disableColumnMenu
                  autoHeight
                  getRowHeight={() => "auto"}
                  disableRowSelectionOnClick
                  pageSizeOptions={[100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100 } },
                  }}
                  sx={{
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #ECECEC",
                      paddingY: "10px",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "#ECECEC",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                      outline: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {events.length === 0 && (
            <Box my={6}>
              <Paper elevation={0}>
                <Box px={2} py={4} textAlign="center">
                  <Typography variant="h5" gutterBottom>
                    {filter === "active" ? "No active events" : "No draft events"}
                  </Typography>
                  <Typography variant="body1" color="textSecondary" gutterBottom>
                    Add an event to create a class booking page for your website.
                  </Typography>
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      component={RouterLink}
                      to="/events/add"
                    >
                      Add Event
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
